import React, { useState, useEffect } from "react"
import { useUser } from "../helpers/userContext"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { FaCheck, FaRegEdit } from "react-icons/fa"
import { MdOutlineCancel } from "react-icons/md"
import { LazyLoadImage } from "react-lazy-load-image-component"
import "react-lazy-load-image-component/src/effects/blur.css"
import { Link } from "react-router-dom"
import profileApi from "../api/profileApi"
import categoriesApi from "../api/categoriesApi"
import portalsApi from "../api/portalsApi"
import { BsBookmarkHeartFill } from "react-icons/bs"
import { BiSolidCommentDetail } from "react-icons/bi"
import { LiaUserEditSolid } from "react-icons/lia"
import Spinner from "../components/spinner"
import { useNavigate } from "react-router-dom";
import ImageWithFallback from "../helpers/ImageWithFallback";
import PortalLogoFallback from "../helpers/PortalLogoFallback";

const ProfilePage = () => {
      const navigate = useNavigate(); // For navigation
  
  const { setUser } = useUser()
  const [editField, setEditField] = useState(null)
  const [userData, setUserData] = useState({
    username: "",
    first_name: "",
    last_name: "",
  })
  const [originalUserData, setOriginalUserData] = useState({ ...userData })
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [showLikedPosts, setShowLikedPosts] = useState(false)
  const [showCommentedPosts, setShowCommentedPosts] = useState(false)
  const { token } = useUser()
  const [likedPosts, setLikedPosts] = useState([])
  const [commentedPosts, setCommentedPosts] = useState([])
  const [categories, setCategories] = useState([])
  const [portals, setPortals] = useState([])

  const [isLoading, setIsLoading] = useState(true)

  // All useEffect hooks at the top level
  useEffect(() => {
    setIsEditingProfile(true)
  }, [])

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsLoading(true) // Start loading
      try {
        const response = await profileApi.getUserProfile()
        const { username, first_name, last_name } = response.data
        setUserData({ username, first_name, last_name })
        setOriginalUserData({ username, first_name, last_name })
      } catch (error) {
        setTimeout(() => {
          toast.error("Failed to fetch user profile. Please try again.", {
            position: "top-center",
          })
        }, 5000)
      } finally {
        setIsLoading(false) // Stop loading
      }
    }

    fetchUserProfile()
  }, [token])

  useEffect(() => {
    const fetchPortals = async () => {
      try {
        const { data } = await portalsApi.getAll()
        setPortals(data)
      } catch (err) {
        console.error("Error fetching portals:", err)
      }
    }
    fetchPortals()
  }, [])

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesApi.getAll()
        setCategories(response.data)
      } catch (error) {
        console.error("Error fetching categories:", error)
      }
    }

    fetchCategories()
  }, [])

  // Use state to control rendering instead of conditional return
  return (
    <div className="bg-gray-100 min-h-screen flex justify-center items-center p-4">
      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <Spinner />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-5xl flex flex-col sm:flex-row">
          <aside className="bg-white w-full sm:w-1/3 p-6 shadow-md mb-6 sm:mb-0 sm:mr-6">
            <div className="text-center sm:text-center">
              <LazyLoadImage
                src="https://www.gravatar.com/avatar/default?d=identicon"
                alt="Profile"
                className="w-32 h-32 rounded-full mx-auto sm:mx-0 mb-4"
                effect="blur"
              />
              <h2 className="text-xl font-bold flex-wrap">
                {originalUserData.username}
              </h2>
            </div>
            <div className="mt-6 space-y-2">
              <button
                className={`w-full py-4 px-4 rounded ${
                  isEditingProfile ? "bg-gray-200" : "hover:bg-gray-200"
                } flex items-center justify-center`}
                onClick={toggleEditProfile}
              >
                <LiaUserEditSolid className="w-6 h-6" />
                <h1 className="mx-5 text-lg font-bold">Edit Profile</h1>
              </button>
              <button
                className={`w-full py-4 px-4 rounded ${
                  showLikedPosts ? "bg-gray-200" : "hover:bg-gray-200"
                } flex items-center justify-center`}
                onClick={toggleLikedPosts}
              >
                <BsBookmarkHeartFill className="w-6 h-6" />
                <h1 className="mx-5 text-lg font-bold">Liked Posts</h1>
              </button>
              <button
                className={`w-full py-4 px-4 rounded ${
                  showCommentedPosts ? "bg-gray-200" : "hover:bg-gray-200"
                } flex items-center justify-center`}
                onClick={toggleCommentedPosts}
              >
                <BiSolidCommentDetail className="w-6 h-6" />
                <h1 className="mx-5 text-lg font-bold">Commented Posts</h1>
              </button>
            </div>
          </aside>
          <div className="flex-1 overflow-hidden w-full">
            {showLikedPosts
              ? renderLikedPosts()
              : showCommentedPosts
              ? renderCommentedPosts()
              : renderEditProfile()}
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  )

  function handleEdit(field) {
    if (editField && editField !== field) {
      setUserData(prevUserData => ({
        ...prevUserData,
        [editField]: originalUserData[editField],
      }))
    }
    setEditField(field)
  }

  async function handleSave(field) {
    try {
      const payload = { [field]: userData[field] }
      const response = await profileApi.updateUserProfile(payload)
      setUser(response.data)
      let fieldName = ""
      switch (field) {
        case "username":
          fieldName = "Username"
          break
        case "first_name":
          fieldName = "First Name"
          break
        case "last_name":
          fieldName = "Last Name"
          break
        default:
          fieldName = "Profile"
      }
      toast.success(`Successfully updated ${fieldName}!`, {
        position: "top-center",
      })
      setEditField(null)
      setOriginalUserData(prev => ({ ...prev, [field]: userData[field] }))
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.username &&
        Array.isArray(error.response.data.username) &&
        error.response.data.username[0] ===
          "A user with that username already exists."
      ) {
        toast.error(
          "This username is already taken. Please choose another one.",
          {
            position: "top-center",
          }
        )
      } else {
        toast.error("Failed to update your profile. Please try again.", {
          position: "top-center",
        })
      }
      setUserData({ ...originalUserData })
      setEditField(null)
    }
  }

  function handleCancel() {
    setUserData({ ...originalUserData })
    setEditField(null)
  }

  function handleChange(e) {
    const { id, value } = e.target
    setUserData(prev => ({ ...prev, [id]: value }))
  }

  function toggleEditProfile() {
    setIsEditingProfile(true)
    setShowLikedPosts(false)
    setShowCommentedPosts(false)
  }

  async function toggleLikedPosts() {
    setShowLikedPosts(true)
    setIsEditingProfile(false)
    setShowCommentedPosts(false)
    await fetchLikedPosts()
  }

  async function toggleCommentedPosts() {
    setShowCommentedPosts(true)
    setIsEditingProfile(false)
    setShowLikedPosts(false)
    await fetchCommentedPosts()
  }

  function renderField(field, label) {
    return (
      <div className="flex flex-col mb-4">
        <label htmlFor={field} className="text-sm font-bold text-gray-700 mb-1">
          {label}
        </label>
        {editField === field ? (
          <div className="flex flex-col sm:flex-row sm:items-center">
            <input
              type={field === "text" ? "text" : "text"} // Assuming text type for simplicity
              id={field}
              value={userData[field]}
              onChange={handleChange}
              className="w-full sm:flex-grow px-3 py-2 border rounded focus:outline-none focus:ring"
            />
            <div className="flex space-x-2 mt-2 sm:mt-0 sm:ml-2">
              <button className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                <FaCheck />
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                <MdOutlineCancel />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-600">{userData[field]}</span>
            <button
              onClick={() => handleEdit(field)}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaRegEdit />
            </button>
          </div>
        )}
      </div>
    )
  }

  function renderEditProfile() {
    return (
      isEditingProfile && (
        <div className="flex-1 bg-white p-6 shadow-md">
          <h1 className="text-2xl font-bold mb-6">Edit Profile</h1>
          <form
            className="space-y-4"
            onSubmit={e => {
              e.preventDefault()
              if (editField) handleSave(editField)
            }}
          >
            {renderField("username", "Username")}
            {renderField("first_name", "First Name")}
            {renderField("last_name", "Last Name")}
          </form>
        </div>
      )
    )
  }

  function getPortalLogo(portalId) {
    return portals.find(portal => portal.id === portalId)?.logo_url || ""
  }

  function handlePortalClick(portalId) {
    const portal = portals.find((p) => p.id === portalId);
    if (portal) {
      const sanitizedPortalName = stripHtmlTags(portal.name)
        .replace(/%20/g, "-")
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9-]/g, "")
        .toLowerCase()
        .trim();

      localStorage.setItem("portalName", sanitizedPortalName);
      localStorage.setItem("portalId", portal.id);
      navigate(`/portal/${sanitizedPortalName}/`); // Use navigate for portal navigation
      window.scrollTo({ top: 0, behavior: "smooth" }); // Smooth scroll to top

    }
  }

  function handleCategoryClick(id, name) {
    const sanitizedCategoryName = name.toLowerCase().replace(/\s+/g, "-");
    localStorage.setItem("categoryName", sanitizedCategoryName);
    localStorage.setItem("categoryId", id);
    navigate(`/category/${sanitizedCategoryName}`); 
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function getCategoryName(categoryId) {
    const category = categories.find(cat => cat.id === categoryId)
    return category ? category.name : ""
  }

  function truncateText(text, limit) {
    return text.length > limit ? text.slice(0, limit) + "..." : text
  }

  function stripHtmlTags(html) {
    return html.replace(/<[^>]+>/g, "")
  }

  function renderLikedPosts() {
    return (
      <section className="p-4 sm:p-6 h-full max-h-[calc(100vh-100px)] max-w-4xl w-full mx-auto flex flex-col">
        <h2 className="text-2xl sm:text-2xl font-bold text-center mb-4 text-gray-800 dark:text-gray-200">
          Artikujt e pelqyer
        </h2>
        <div className="flex flex-col gap-4 sm:gap-5 flex-1 overflow-y-auto max-h-[calc(100vh-180px)] w-full">
          {likedPosts.map(article => {
            const portalLogo = getPortalLogo(article.portal)
            const portalName = portals.find(portal => portal.id === article.portal)?.name || ""
            const categoryName = getCategoryName(
              article?.categories_related?.[0]
            )

            return (
              <div
                key={article.id}
                className="flex items-start sm:items-center gap-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow w-full"
              >
                <LazyLoadImage
                  src={article.thumbnail || "https://via.placeholder.com/150"}
                  alt={article.title}
                  effect="blur"
                  className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-md flex-shrink-0"
                />

                <div className="flex-1">
                  <h3 className="text-sm sm:text-base font-semibold flex items-center justify-between">
                    <Link
                      to={`/article/${article?.id}?categoryId=${article?.categories_related?.[0]}`}
                      className={`text-customOrange dark:text-blue-400 hover:underline mr-2 ${
                        article.title.length > 40 ? "block" : "truncate"
                      }`}
                    >
                      {truncateText(article.title, 60)}
                    </Link>

                    {portalLogo && (
                      <ImageWithFallback src={portalLogo} className="w-8 h-8 object-contain rounded-full border border-gray-300 cursor-pointer flex-shrink-0" fallback={<PortalLogoFallback portalName={portalName} isOverlay={false} />} onClick={() => handlePortalClick(article.portal)} />
                      
                    
                    )}
                  </h3>
                  <p
                    className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2 cursor-pointer hover:underline"
                    onClick={() =>
                      handleCategoryClick(
                        article?.categories_related?.[0],
                        getCategoryName(article?.categories_related?.[0])
                      )
                    }
                  >
                    {categoryName}
                  </p>

                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                    {truncateText(stripHtmlTags(article.content), 100)}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  function renderCommentedPosts() {
    return (
      <section className="p-4 sm:p-6 max-h-full flex flex-col justify-between">
        <h2 className="text-2xl sm:text-2xl font-bold text-center mb-4 text-gray-800 dark:text-gray-200">
          Artikujt e Komentuar
        </h2>
        <div className="flex flex-col gap-4 sm:gap-5 flex-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          {commentedPosts.map(article => {
            const portalLogo = getPortalLogo(article.portal)
            const portalName = portals.find(portal => portal.id === article.portal)?.name || ""
            const categoryName = getCategoryName(
              article?.categories_related?.[0]
            )

            return (
              <div
                key={article.id}
                className="flex items-center gap-4 bg-white dark:bg-gray-800 rounded-lg shadow p-3 hover:shadow-md transition-shadow"
              >
                <LazyLoadImage
                  src={article.thumbnail || "https://via.placeholder.com/150"}
                  alt={article.title}
                  effect="blur"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md flex-shrink-0"
                />
                <div className="flex-1">
                  <h3 className="text-sm sm:text-base font-semibold flex items-center justify-between">
                    <Link
                      to={`/article/${article?.id}?categoryId=${article?.categories_related?.[0]}`}
                      className="text-customOrange dark:text-blue-400 hover:underline mr-2"
                    >
                      {truncateText(article.title, 60)}
                    </Link>
                    {portalLogo && (
                      <ImageWithFallback src={portalLogo} className="w-8 h-8 object-contain rounded-full border border-gray-300 cursor-pointer flex-shrink-0" fallback={<PortalLogoFallback portalName={portalName} isOverlay={false} />} onClick={() => handlePortalClick(article.portal)} />
                      
                    
                    )}
                  </h3>
                  <p
                    className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2 cursor-pointer hover:underline"
                    onClick={() =>
                      handleCategoryClick(
                        article?.categories_related?.[0],
                        getCategoryName(article?.categories_related?.[0])
                      )
                    }
                  >
                    {categoryName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                    {stripHtmlTags(article.content)}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    )
  }

  async function fetchLikedPosts() {
    try {
      const response = await profileApi.profileLiked(token)
      setLikedPosts(response.data)
    } catch (error) {
      toast.error("Failed to fetch liked posts. Please try again.", {
        position: "top-center",
      })
    }
  }

  async function fetchCommentedPosts() {
    try {
      const response = await profileApi.profileCommented(token)
      setCommentedPosts(response.data)
    } catch (error) {
      toast.error("Failed to fetch commented posts. Please try again.", {
        position: "top-center",
      })
    }
  }
}

export default ProfilePage
